import React, { useEffect, useRef, useState } from 'react';
import { getSocket } from '../../hooks/useSocket';
import axios from 'axios';
import { API_URL, getAuthHeader } from '../../utils/config';

const ICE_CONFIG = {
  iceServers: [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
    { urls: ['turn:35.154.186.85:3478','turn:35.154.186.85:3478?transport=tcp'], username:'qtalk', credential:'Qtalk@2026' }
  ]
};

const playTone = (type) => {
  try {
    const ctx = new (window.AudioContext||window.webkitAudioContext)();
    const freq = type==='incoming'?880:type==='outgoing'?440:480;
    const interval = type==='incoming'?2000:type==='outgoing'?3000:500;
    const beep = () => {
      const o=ctx.createOscillator(), g=ctx.createGain();
      o.connect(g); g.connect(ctx.destination);
      o.frequency.value=freq;
      g.gain.setValueAtTime(0.3,ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.001,ctx.currentTime+0.8);
      o.start(ctx.currentTime); o.stop(ctx.currentTime+0.8);
    };
    beep();
    const iv = setInterval(beep, interval);
    return () => { clearInterval(iv); try{ctx.close();}catch(e){} };
  } catch(e) { return ()=>{}; }
};

const fmt = (s) => `${String(Math.floor(s/60)).padStart(2,'0')}:${String(s%60).padStart(2,'0')}`;

const Avatar = ({name, url, size=96, pulse=false}) => (
  <div style={{position:'relative',width:size+48,height:size+48,display:'flex',alignItems:'center',justifyContent:'center'}}>
    {pulse && <>
      <div style={{position:'absolute',inset:0,borderRadius:'50%',border:'2px solid rgba(13,148,136,0.5)',animation:'rp 2s ease-out infinite'}}/>
      <div style={{position:'absolute',inset:-16,borderRadius:'50%',border:'2px solid rgba(13,148,136,0.25)',animation:'rp 2s ease-out infinite',animationDelay:'0.6s'}}/>
    </>}
    <div style={{width:size,height:size,borderRadius:'50%',overflow:'hidden',background:'linear-gradient(135deg,#0d9488,#2563eb)',
      display:'flex',alignItems:'center',justifyContent:'center',fontSize:size*0.38,fontWeight:900,color:'white',
      border:'3px solid rgba(255,255,255,0.15)',boxShadow:'0 8px 32px rgba(0,0,0,0.5)'}}>
      {url?.startsWith('http') ? <img src={url} style={{width:'100%',height:'100%',objectFit:'cover'}} alt=""/>
        : url?.startsWith('/uploads') ? <img src={`https://qtalk.qualtechedge.in${url}`} style={{width:'100%',height:'100%',objectFit:'cover'}} alt=""/>
        : (name?.[0]||'?').toUpperCase()}
    </div>
  </div>
);

const Btn = ({onClick, red, green, teal, label, children}) => (
  <div style={{display:'flex',flexDirection:'column',alignItems:'center',gap:6}}>
    <button onClick={onClick} style={{width:54,height:54,borderRadius:'50%',border:'none',cursor:'pointer',
      background:red?'rgba(239,68,68,0.85)':green?'rgba(16,185,129,0.85)':teal?'rgba(13,148,136,0.85)':'rgba(255,255,255,0.18)',
      display:'flex',alignItems:'center',justifyContent:'center',transition:'all 0.2s',
      outline:'1px solid rgba(255,255,255,0.1)'}}>
      {children}
    </button>
    {label && <span style={{color:'rgba(255,255,255,0.45)',fontSize:10,whiteSpace:'nowrap'}}>{label}</span>}
  </div>
);

export default function CallModal({ currentUser, targetUser, callType, isInitiating, call, onClose }) {
  const [state, setState] = useState(isInitiating?'calling':'incoming');
  const [minimized, setMinimized] = useState(false);
  const [isMuted, setMuted] = useState(false);
  const [isVidOff, setVidOff] = useState(false);
  const [duration, setDuration] = useState(0);
  const [statusTxt, setStatus] = useState(isInitiating?'Dialing...':'Incoming call');

  const localVid = useRef(null);
  const remoteVid = useRef(null);
  const remoteAudio = useRef(null);
  const pc = useRef(null);
  const stream = useRef(null);
  const timer = useRef(null);
  const ringStop = useRef(null);
  const durRef = useRef(0);
  const connectedRef = useRef(false);
  useEffect(()=>{ durRef.current=duration; },[duration]);

  // Listen for group call invite from the other party
  useEffect(() => {
    const s = getSocket();
    if (!s) return;
    const handler = ({ meetUrl, fromName }) => {
      setShowUpgradePrompt(true);
      setUpgradeMeetUrl(meetUrl);
    };
    s.on('call_group_invite', handler);
    return () => s.off('call_group_invite', handler);
  }, []);

  const stopRing = () => { if(ringStop.current){ringStop.current();ringStop.current=null;} };
  const startTimer = () => { if(timer.current) return; timer.current=setInterval(()=>setDuration(d=>d+1),1000); };
  const cleanup = () => {
    stopRing();
    if(timer.current){clearInterval(timer.current);timer.current=null;}
    if(stream.current){stream.current.getTracks().forEach(t=>t.stop());stream.current=null;}
    if(pc.current){pc.current.close();pc.current=null;}
    if(localVid.current) localVid.current.srcObject=null;
    if(remoteVid.current) remoteVid.current.srcObject=null;
  };

  const getMedia = async () => {
    try {
      const s = await navigator.mediaDevices.getUserMedia({video:callType==='video',audio:true});
      stream.current = s;
      if(localVid.current) localVid.current.srcObject = s;
      return s;
    } catch(e) { setState('failed'); return null; }
  };

  const createPC = (mediaStream) => {
    const p = new RTCPeerConnection(ICE_CONFIG);
    mediaStream.getTracks().forEach(t => p.addTrack(t, mediaStream));
    const remote = new MediaStream();
    p.ontrack = e => {
      remote.addTrack(e.track);
      if(remoteVid.current) remoteVid.current.srcObject = remote;
      if(remoteAudio.current) remoteAudio.current.srcObject = remote;
    };
    p.oniceconnectionstatechange = () => {
      console.log('ICE:', p.iceConnectionState);
      if(p.iceConnectionState==='connected'||p.iceConnectionState==='completed') {
        setState('connected'); setStatus('Connected'); stopRing(); startTimer();
        connectedRef.current = true;
      } else if(p.iceConnectionState==='failed') {
        // Try ICE restart before giving up
        console.log('ICE failed, attempting restart...');
        try { p.restartIce(); } catch(e) {
          setState('failed'); cleanup(); setTimeout(onClose,2000);
        }
      } else if(p.iceConnectionState==='disconnected') {
        // Temporary disconnection - wait for reconnect
        console.log('ICE disconnected, waiting...');
        setTimeout(() => {
          if(p.iceConnectionState==='disconnected') {
            try { p.restartIce(); } catch(e) {}
          }
        }, 3000);
      }
    };
    return p;
  };

  const waitForICE = (p) => new Promise(res => {
    if(p.iceGatheringState==='complete'){res();return;}
    const check = ()=>{ if(p.iceGatheringState==='complete'){p.onicegatheringstatechange=null;res();} };
    p.onicegatheringstatechange = check;
    setTimeout(res, 4000);
  });

  const logCall = (status) => {
    if (!isInitiating) return;
    const actualDuration = connectedRef.current ? durRef.current : 0;
    const actualStatus = connectedRef.current ? 'completed' : status;
    try { 
      axios.post(`${API_URL}/users/calls/log`,{
        receiver_id: targetUser.id,
        call_type: callType,
        status: actualStatus,
        duration_seconds: actualDuration
      },{headers:getAuthHeader()}); 
    } catch(e){}
  };

  // Initiator
  useEffect(() => {
    if(!isInitiating) return;
    const sock = getSocket();
    ringStop.current = playTone('outgoing');
    let texts=['Dialing...','Ringing...','Waiting...'],i=0;
    const anim = setInterval(()=>{setStatus(texts[++i%texts.length]);},2500);

    (async()=>{
      const s = await getMedia();
      if(!s) return;
      const p = createPC(s);
      pc.current = p;
      const offer = await p.createOffer();
      await p.setLocalDescription(offer);
      await waitForICE(p);
      sock.emit('call_offer',{to:targetUser.id,offer:p.localDescription,callType});

      sock.on('call_answered',async({answer})=>{
        try{ await p.setRemoteDescription(new RTCSessionDescription(answer)); }catch(e){console.error(e);}
      });
      sock.on('call_ice',async({candidate})=>{ try{if(candidate) await p.addIceCandidate(new RTCIceCandidate(candidate));}catch(e){} });
      sock.on('call_rejected',()=>{ clearInterval(anim); stopRing(); logCall('no_answer'); setState('rejected'); cleanup(); setTimeout(onClose,2000); });
      sock.on('call_ended',()=>{ clearInterval(anim); stopRing(); logCall(connectedRef.current?'completed':'no_answer'); setState('ended'); cleanup(); setTimeout(onClose,1500); });
      sock.on('call_no_answer',()=>{ clearInterval(anim); logCall('no_answer'); setState('no_answer'); cleanup(); setTimeout(onClose,2500); });
      sock.on('call_busy',()=>{ clearInterval(anim); setState('busy'); cleanup(); setTimeout(onClose,2000); });
    })();

    return ()=>{ clearInterval(anim); cleanup(); };
  },[]);

  // Receiver
  useEffect(()=>{
    if(isInitiating) return;
    const sock = getSocket();
    ringStop.current = playTone('incoming');
    sock.on('call_ended',()=>{ setState('ended'); cleanup(); setTimeout(onClose,1500); });
    sock.on('call_timeout',()=>{ setState('no_answer'); cleanup(); setTimeout(onClose,1500); });
    return ()=>{ stopRing(); };
  },[]);

  const acceptCall = async () => {
    stopRing();
    setState('connecting'); setStatus('Connecting...');
    const sock = getSocket();
    const s = await getMedia();
    if(!s) return;
    const p = createPC(s);
    pc.current = p;
    await p.setRemoteDescription(new RTCSessionDescription(call.offer));
    const answer = await p.createAnswer();
    await p.setLocalDescription(answer);
    await waitForICE(p);
    sock.emit('call_answer',{to:targetUser.id,answer:p.localDescription});
  };

  const rejectCall = () => {
    stopRing();
    getSocket().emit('call_reject',{to:targetUser.id});
    logCall('rejected'); cleanup(); onClose();
  };

  const endCall = () => {
    getSocket().emit('call_end',{to:targetUser.id});
    logCall(connectedRef.current ? 'completed' : 'no_answer');
    setState('ended'); cleanup(); setTimeout(onClose,1000);
  };

  const toggleMute = () => { stream.current?.getAudioTracks().forEach(t=>{t.enabled=isMuted;}); setMuted(!isMuted); };
  const toggleVid = () => { stream.current?.getVideoTracks().forEach(t=>{t.enabled=isVidOff;}); setVidOff(!isVidOff); };

  const [showAddUser, setShowAddUser] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [allUsers, setAllUsers] = useState([]);
  const [invitedUsers, setInvitedUsers] = useState([]); // [{id, name, avatar, status}]
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [upgradeMeetUrl, setUpgradeMeetUrl] = useState('');

  // Fetch users for add participant
  const fetchUsers = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/users`, { headers: getAuthHeader() });
      setAllUsers(data.filter(u => u.id !== currentUser?.id && u.id !== targetUser?.id));
    } catch(e) {}
  };

  const addParticipant = async (u) => {
    setShowAddUser(false);
    try {
      // Get current user's meet link from localStorage
      const storedUser = JSON.parse(localStorage.getItem('qtalk_user') || '{}');
      const meetLink = storedUser?.meet_link;
      if (!meetLink) {
        alert('Could not find your meeting room. Please update your profile.');
        return;
      }
      const meetUrl = `https://qtalk.qualtechedge.in/meet/${meetLink}`;
      const msgText = `📹 ${storedUser.full_name} is inviting you to a group call. Join here: ${meetUrl}`;

      // Send invite message to the new participant via DM
      const dmRes = await axios.post(`${API_URL}/chat/conversations/direct`,
        { user_id: u.id }, { headers: getAuthHeader() });
      const dmConvId = dmRes.data.conversation_id;

      // Send meet link to new participant's DM
      const s = getSocket();
      if (s) {
        s.emit('send_message', {
          conversation_id: dmConvId,
          content: msgText,
          message_type: 'text'
        });
      }

      // Also send to current conversation if available
      const currentConvId = window.__qtalk_active_conv_id__;
      if (currentConvId && currentConvId !== dmConvId && s) {
        s.emit('send_message', {
          conversation_id: currentConvId,
          content: msgText,
          message_type: 'text'
        });
      }

      // Add to invited list with 'invited' status
      setInvitedUsers(prev => [...prev.filter(x => x.id !== u.id),
        { id: u.id, name: u.full_name, avatar: u.avatar_url, status: 'invited' }]);

      // Show upgrade prompt to host
      setShowUpgradePrompt(true);
      setUpgradeMeetUrl(meetUrl);

      // Also notify the current call participant (User B) via socket
      if (targetUser?.id && s) {
        s.emit('call_group_invite', {
          to: targetUser.id,
          meetUrl,
          fromName: storedUser.full_name
        });
      }

    } catch(e) {
      console.error('Invite error:', e);
      alert('Failed to send invite. Please try again.');
    }
  };

  const isConnected = state==='connected';
  const isIncoming = state==='incoming';
  const isCalling = state==='calling'||state==='connecting';
  const isTerminal = ['ended','rejected','failed','busy','no_answer'].includes(state);
  const termLabel = {ended:'Call Ended',rejected:'Call Declined',failed:'Call Failed',busy:'User is Busy',no_answer:'No Answer'}[state];

  // MINIMIZED pill
  if(minimized) return (
    <div style={{position:'fixed',bottom:24,right:24,zIndex:9999}}>
      <div onClick={()=>setMinimized(false)} style={{display:'flex',alignItems:'center',gap:10,
        background:'linear-gradient(135deg,#0d9488,#1e40af)',borderRadius:50,padding:'8px 14px 8px 8px',
        boxShadow:'0 8px 32px rgba(0,0,0,0.5)',cursor:'pointer',border:'1px solid rgba(255,255,255,0.15)'}}>
        <div style={{width:36,height:36,borderRadius:'50%',background:'rgba(255,255,255,0.15)',overflow:'hidden',flexShrink:0,display:'flex',alignItems:'center',justifyContent:'center',fontSize:14,fontWeight:700,color:'white'}}>
          {targetUser?.avatar_url
              ? <img src={targetUser.avatar_url.startsWith('http') ? targetUser.avatar_url : `https://qtalk.qualtechedge.in${targetUser.avatar_url}`} style={{width:'100%',height:'100%',objectFit:'cover'}} alt="" onError={e=>e.target.style.display='none'}/>
              : targetUser?.full_name?.[0]?.toUpperCase()}
        </div>
        <div>
          <p style={{color:'white',fontSize:12,fontWeight:700,margin:0}}>{targetUser?.full_name}</p>
          <p style={{color:'rgba(255,255,255,0.7)',fontSize:10,margin:0}}>{isConnected?fmt(duration):isCalling?'Calling...':'Incoming...'}</p>
        </div>
        <button onClick={e=>{e.stopPropagation();endCall();}} style={{width:30,height:30,borderRadius:'50%',border:'none',background:'#ef4444',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',marginLeft:4}}>
          <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{width:14,height:14,transform:'rotate(135deg)'}}><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.8 19.79 19.79 0 01.22 1.18a2 2 0 012-.22h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.91 8.79a16 16 0 006.29 6.29l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/></svg>
        </button>
      </div>
    </div>
  );

  return (
    <div style={{position:'fixed',inset:0,zIndex:9999,display:'flex',alignItems:'center',justifyContent:'center',
      background:'rgba(0,0,0,0.82)',backdropFilter:'blur(18px)'}}>
      {/* Remote video bg */}
      {callType==='video' && isConnected && <video ref={remoteVid} autoPlay playsInline style={{position:'absolute',inset:0,width:'100%',height:'100%',objectFit:'cover',opacity:0.35}}/>}

      {/* Hidden audio element for voice calls */}
      <audio ref={remoteAudio} autoPlay style={{display:'none'}}/>

      {/* Add Participant Search Overlay */}
      {showAddUser && (
        <div style={{
          position:'fixed', bottom:160, left:'50%', transform:'translateX(-50%)',
          width:300, background:'rgba(10,18,35,0.99)', borderRadius:20,
          border:'1px solid rgba(255,255,255,0.15)', backdropFilter:'blur(20px)',
          boxShadow:'0 8px 40px rgba(0,0,0,0.8)', zIndex:10001, overflow:'hidden'
        }}>
          <div onClick={e=>e.stopPropagation()} style={{padding:'14px 14px 8px'}}>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:10}}>
              <p style={{color:'white',fontWeight:700,fontSize:14,margin:0}}>Add to call</p>
              <button onClick={()=>setShowAddUser(false)} style={{background:'none',border:'none',cursor:'pointer',
                color:'rgba(255,255,255,0.4)',padding:4,display:'flex',alignItems:'center',justifyContent:'center'}}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" style={{width:16,height:16}}>
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
            <div style={{position:'relative'}}>
              <svg viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="2"
                style={{position:'absolute',left:10,top:'50%',transform:'translateY(-50%)',width:15,height:15}}>
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <input
                autoFocus
                type="text"
                placeholder="Search people..."
                value={userSearch}
                onChange={e => setUserSearch(e.target.value)}
                style={{
                  width:'100%', padding:'8px 10px 8px 32px',
                  background:'rgba(255,255,255,0.08)', border:'1px solid rgba(255,255,255,0.1)',
                  borderRadius:12, color:'white', fontSize:13, outline:'none',
                  boxSizing:'border-box'
                }}
              />
            </div>
          </div>
          <div onClick={e=>e.stopPropagation()} style={{maxHeight:200, overflowY:'auto', padding:'4px 8px 10px'}}>
            {allUsers
              .filter(u => u.full_name.toLowerCase().includes(userSearch.toLowerCase()))
              .map(u => (
                <div key={u.id}
                  onClick={() => addParticipant(u)}
                  style={{
                    display:'flex', alignItems:'center', gap:10,
                    padding:'8px 8px', borderRadius:12, cursor:'pointer',
                    transition:'background 0.15s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background='rgba(255,255,255,0.08)'}
                  onMouseLeave={e => e.currentTarget.style.background='transparent'}
                >
                  <div style={{
                    width:34, height:34, borderRadius:'50%', flexShrink:0,
                    background:'linear-gradient(135deg,#0d9488,#2563eb)',
                    display:'flex', alignItems:'center', justifyContent:'center',
                    fontSize:14, fontWeight:700, color:'white', overflow:'hidden'
                  }}>
                    {u.avatar_url
                      ? <img src={u.avatar_url.startsWith('http')?u.avatar_url:`https://qtalk.qualtechedge.in${u.avatar_url}`}
                          style={{width:'100%',height:'100%',objectFit:'cover'}} alt=""
                          onError={e=>e.target.style.display='none'}/>
                      : (u.full_name?.[0]||'?').toUpperCase()}
                  </div>
                  <div style={{flex:1,minWidth:0}}>
                    <p style={{color:'white',fontWeight:600,fontSize:13,margin:0,
                      whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>
                      {u.full_name}
                    </p>
                    <p style={{color:'rgba(255,255,255,0.4)',fontSize:11,margin:0}}>
                      {u.designation || u.department || u.email}
                    </p>
                  </div>
                  <div style={{
                    width:26,height:26,borderRadius:'50%',
                    background:'rgba(13,148,136,0.8)',
                    display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0
                  }}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5"
                      strokeLinecap="round" strokeLinejoin="round" style={{width:13,height:13}}>
                      <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                    </svg>
                  </div>
                </div>
              ))}
            {allUsers.filter(u => u.full_name.toLowerCase().includes(userSearch.toLowerCase())).length === 0 && (
              <p style={{color:'rgba(255,255,255,0.3)',fontSize:13,textAlign:'center',padding:'16px 0',margin:0}}>
                No users found
              </p>
            )}
          </div>
        </div>
      )}
      {/* Card */}
      <div style={{position:'relative',zIndex:10,width:'100%',maxWidth:340,margin:'0 16px',borderRadius:28,
        background:'linear-gradient(160deg,rgba(10,20,40,0.95),rgba(5,10,25,0.98))',
        backdropFilter:'blur(24px)',border:'1px solid rgba(255,255,255,0.08)',
        boxShadow:'0 32px 80px rgba(0,0,0,0.7)',overflow:'hidden'}}>

        {/* Top gradient bar */}
        <div style={{height:4,background:'linear-gradient(90deg,#0d9488,#2563eb,#7c3aed)'}}/>

        <div style={{padding:'28px 24px 24px',textAlign:'center'}}>
          {/* Minimize btn */}
          {!isTerminal && (
            <button onClick={()=>setMinimized(true)} style={{position:'absolute',top:12,right:12,background:'rgba(255,255,255,0.08)',border:'none',borderRadius:8,padding:'4px 8px',cursor:'pointer',color:'rgba(255,255,255,0.5)',fontSize:10,display:'flex',alignItems:'center',gap:3}}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{width:10,height:10}}><polyline points="4 14 10 14 10 20"/><polyline points="20 10 14 10 14 4"/><line x1="10" y1="14" x2="21" y2="3"/><line x1="3" y1="21" x2="14" y2="10"/></svg>
              min
            </button>
          )}

          {/* Call type badge */}
          <div style={{display:'inline-flex',alignItems:'center',gap:5,background:'rgba(13,148,136,0.12)',borderRadius:20,padding:'3px 10px',marginBottom:16}}>
            <svg viewBox="0 0 24 24" fill="none" stroke="#0d9488" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{width:11,height:11}}>
              {callType==='video'?<><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2"/></>:<><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.8 19.79 19.79 0 01.22 1.18a2 2 0 012-.22h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.91 8.79a16 16 0 006.29 6.29l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/></>}
            </svg>
            <span style={{color:'#0d9488',fontSize:10,fontWeight:600}}>{callType==='video'?'Video Call':'Voice Call'}</span>
          </div>

          <div style={{display:'flex',justifyContent:'center',marginBottom:4}}>
            <Avatar name={targetUser?.full_name} url={targetUser?.avatar_url} size={84} pulse={isCalling||isIncoming}/>
          </div>

          <p style={{color:'white',fontSize:19,fontWeight:700,margin:'12px 0 4px',letterSpacing:'-0.3px'}}>{targetUser?.full_name}</p>
          <p style={{color:isConnected?'#0d9488':'rgba(255,255,255,0.45)',fontSize:13,margin:'0 0 16px',fontWeight:isConnected?600:400}}>
            {isConnected?fmt(duration):isTerminal?termLabel:statusTxt}
          </p>

          {/* Invited participants */}
          {invitedUsers.length > 0 && (
            <div style={{display:'flex',flexDirection:'column',gap:8,width:'100%',
              padding:'10px 16px',marginBottom:12,
              background:'rgba(255,255,255,0.04)',borderRadius:16,
              border:'1px solid rgba(255,255,255,0.06)'}}>
              {invitedUsers.map(u => {
                const avatarSrc = u.avatar ? (u.avatar.startsWith('http') ? u.avatar : `https://qtalk.qualtechedge.in${u.avatar}`) : null;
                return (
                  <div key={u.id} style={{display:'flex',alignItems:'center',gap:10}}>
                    <div style={{width:32,height:32,borderRadius:'50%',flexShrink:0,overflow:'hidden',
                      background:'linear-gradient(135deg,#0d9488,#2563eb)',
                      display:'flex',alignItems:'center',justifyContent:'center',
                      fontSize:13,fontWeight:700,color:'white',
                      border: u.status==='ringing' ? '2px solid rgba(13,148,136,0.8)' : '2px solid rgba(255,255,255,0.1)'}}>
                      {avatarSrc
                        ? <img src={avatarSrc} style={{width:'100%',height:'100%',objectFit:'cover'}} alt=""
                            onError={e=>e.target.style.display='none'}/>
                        : (u.name?.[0]||'?').toUpperCase()}
                    </div>
                    <div style={{flex:1,minWidth:0}}>
                      <p style={{color:'white',fontSize:13,fontWeight:600,margin:0,
                        whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{u.name}</p>
                    </div>
                    <span style={{fontSize:11,fontWeight:600,padding:'2px 8px',borderRadius:10,
                      background: u.status==='invited' ? 'rgba(13,148,136,0.2)' : u.status==='failed' ? 'rgba(239,68,68,0.2)' : 'rgba(255,255,255,0.1)',
                      color: u.status==='invited' ? '#2dd4bf' : u.status==='failed' ? '#f87171' : 'rgba(255,255,255,0.5)'
                    }}>
                      {u.status==='invited' ? '✓ Invited' : u.status==='failed' ? 'Failed' : u.status}
                    </span>
                  </div>
                );
              })}
            </div>
          )}

          {/* Conference upgrade prompt */}
          {showUpgradePrompt && upgradeMeetUrl && (
            <div style={{width:'100%',padding:'12px 14px',marginBottom:12,
              background:'rgba(13,148,136,0.1)',borderRadius:14,
              border:'1px solid rgba(13,148,136,0.25)',textAlign:'left'}}>
              <p style={{color:'#2dd4bf',fontSize:12,fontWeight:700,margin:'0 0 4px',display:'flex',alignItems:'center',gap:5}}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{width:12,height:12}}><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/></svg>
                Group call via Qtalk Meet
              </p>
              <p style={{color:'rgba(255,255,255,0.5)',fontSize:11,margin:'0 0 10px',lineHeight:1.5}}>
                Invite sent! For the best group call experience, upgrade this call to a Qtalk Meet room — all participants can join with full video.
              </p>
              <div style={{display:'flex',gap:6}}>
                <a href={upgradeMeetUrl} target="_blank" rel="noreferrer"
                  style={{flex:1,padding:'7px 10px',borderRadius:9,background:'linear-gradient(135deg,#0d9488,#2563eb)',
                    color:'white',textDecoration:'none',fontSize:11,fontWeight:700,textAlign:'center',display:'block'}}>
                  🎥 Join Meet Room
                </a>
                <button onClick={()=>setShowUpgradePrompt(false)}
                  style={{padding:'7px 10px',borderRadius:9,border:'1px solid rgba(255,255,255,0.1)',
                    background:'transparent',color:'rgba(255,255,255,0.4)',fontSize:11,cursor:'pointer'}}>
                  Dismiss
                </button>
              </div>
            </div>
          )}

          {/* Local video pip */}
          {callType==='video' && (isConnected||isCalling) && (
            <video ref={localVid} autoPlay muted playsInline style={{width:72,height:54,borderRadius:8,objectFit:'cover',position:'absolute',bottom:80,right:16,border:'2px solid rgba(255,255,255,0.2)'}}/>
          )}

          {/* Controls */}
          {isIncoming && (
            <div style={{display:'flex',justifyContent:'center',gap:40}}>
              <Btn onClick={rejectCall} red label="Decline">
                <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{width:26,height:26,transform:'rotate(135deg)'}}><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.8 19.79 19.79 0 01.22 1.18a2 2 0 012-.22h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.91 8.79a16 16 0 006.29 6.29l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/></svg>
              </Btn>
              <Btn onClick={acceptCall} green label="Accept">
                <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{width:26,height:26}}><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.8 19.79 19.79 0 01.22 1.18a2 2 0 012-.22h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.91 8.79a16 16 0 006.29 6.29l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/></svg>
              </Btn>
            </div>
          )}

          {(isCalling||isConnected) && (
            <div style={{display:'flex',justifyContent:'center',gap:16}}>
              <Btn onClick={toggleMute} label={isMuted?'Unmute':'Mute'}>
                <svg viewBox="0 0 24 24" fill="none" stroke={isMuted?'#ef4444':'white'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{width:22,height:22}}>
                  {isMuted?<><line x1="1" y1="1" x2="23" y2="23"/><path d="M9 9v3a3 3 0 005.12 2.12M15 9.34V4a3 3 0 00-5.94-.6"/><path d="M17 16.95A7 7 0 015 12v-2m14 0v2a7 7 0 01-.11 1.23M12 19v4M8 23h8"/></>:<><path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z"/><path d="M19 10v2a7 7 0 01-14 0v-2M12 19v4M8 23h8"/></>}
                </svg>
              </Btn>
              {callType==='video' && (
                <Btn onClick={toggleVid} label={isVidOff?'Start Video':'Stop Video'}>
                  <svg viewBox="0 0 24 24" fill="none" stroke={isVidOff?'#ef4444':'white'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{width:22,height:22}}>
                    {isVidOff?<><line x1="1" y1="1" x2="23" y2="23"/><path d="M21 21H3a2 2 0 01-2-2V8a2 2 0 012-2h3m3-3h6l2 3h4a2 2 0 012 2v9.34m-7.72-2.06a4 4 0 11-5.56-5.56"/></>:<><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2"/></>}
                  </svg>
                </Btn>
              )}
              {isConnected && <Btn onClick={() => { setShowAddUser(s=>!s); if(!allUsers.length) fetchUsers(); }} teal label="Add">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{width:22,height:22}}>
                  <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
                  <line x1="19" y1="8" x2="19" y2="14"/><line x1="22" y1="11" x2="16" y2="11"/>
                </svg>
              </Btn>}
              <Btn onClick={endCall} red label="End">
                <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{width:22,height:22,transform:'rotate(135deg)'}}><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.8 19.79 19.79 0 01.22 1.18a2 2 0 012-.22h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.91 8.79a16 16 0 006.29 6.29l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/></svg>
              </Btn>
            </div>
          )}

          {isTerminal && (
            <button onClick={onClose} style={{padding:'10px 28px',borderRadius:12,border:'1px solid rgba(255,255,255,0.15)',background:'transparent',color:'rgba(255,255,255,0.7)',cursor:'pointer',fontSize:13,fontWeight:600}}>
              Close
            </button>
          )}
        </div>
      </div>
      <style>{`@keyframes rp{0%{transform:scale(1);opacity:0.5}100%{transform:scale(1.8);opacity:0}}`}</style>
    </div>
  );
}
